import type { TenantSettings } from '../../api/endpoints.js';

/**
 * The main Settings form: its values, how they load, and the body a save sends.
 *
 * Kept apart from the page so the rules that decide what reaches the server —
 * above all how an optional bilingual pair is sent (D204) — can be tested
 * without rendering anything.
 */

/**
 * Empty until the laboratory's own settings arrive.
 *
 * Deliberately NOT pre-filled with 'ar', 'Asia/Riyadh', 'SAR' or 15%. Those are
 * the DEFAULTS FOR A NEW ORGANISATION and they belong to the backend, which
 * applies them when a laboratory is created. Restating them here would be a
 * second copy of the same policy, free to drift — and, worse, it would briefly
 * show one laboratory's values to another. The form does not render until the
 * data has loaded, so these blanks are never seen.
 */
export const EMPTY_SETTINGS_FORM = {
  nameAr: '',
  nameEn: '',
  addressAr: '',
  addressEn: '',
  contactEmail: '',
  contactPhone: '',
  defaultLocale: '',
  timezone: '',
  currency: '',
  taxRatePercent: null as number | null,
  requirePaymentBeforeDelivery: false,
  allowPartialReports: false,
  showHijriDates: false,
  // The laboratory's OWN registration numbers (Phase 4). Blank until its
  // settings arrive: they belong to the laboratory, never to the product.
  vatRegistrationNumber: '',
  commercialRegistrationNumber: '',
  /** Which client fields THIS laboratory insists on. Empty by default. */
  clientRequiredFields: [] as string[],
  /**
   * The laboratory's OWN quotation policy (Phase 5). Loaded from its settings;
   * the product's default for a NEW laboratory lives in one place — the
   * backend — and is never restated here (CLAUDE.md section 6.1).
   */
  quotationValidityDays: null as number | null,
  quotationTermsAr: '',
  quotationTermsEn: '',
  /**
   * What the quotation document prints about the laboratory (D199). Each is
   * this laboratory's own, empty until set; the product has no default.
   */
  cityAr: '',
  cityEn: '',
  countryCode: '',
  website: '',
  bankAccountName: '',
  bankName: '',
  bankAccountNumber: '',
  bankIban: '',
  bankSwiftCode: '',
  quotationIntroductionAr: '',
  quotationIntroductionEn: '',
  quotationPaymentTermsAr: '',
  quotationPaymentTermsEn: '',
  quotationFooterTextAr: '',
  quotationFooterTextEn: '',
};

export type SettingsFormValues = typeof EMPTY_SETTINGS_FORM;

export function settingsFormFrom(data: TenantSettings): SettingsFormValues {
  return {
    nameAr: data.tenant.nameAr ?? '',
    nameEn: data.tenant.nameEn ?? '',
    addressAr: data.addressAr ?? '',
    addressEn: data.addressEn ?? '',
    contactEmail: data.contactEmail ?? '',
    contactPhone: data.contactPhone ?? '',
    defaultLocale: data.defaultLocale,
    timezone: data.timezone,
    currency: data.currency,
    taxRatePercent: data.taxRatePercent,
    requirePaymentBeforeDelivery: data.requirePaymentBeforeDelivery,
    allowPartialReports: data.allowPartialReports,
    showHijriDates: data.showHijriDates,
    vatRegistrationNumber: data.vatRegistrationNumber ?? '',
    commercialRegistrationNumber: data.commercialRegistrationNumber ?? '',
    clientRequiredFields: data.clientRequiredFields ?? [],
    quotationValidityDays: data.quotationValidityDays,
    quotationTermsAr: data.quotationTerms?.ar ?? '',
    quotationTermsEn: data.quotationTerms?.en ?? '',
    cityAr: data.city?.ar ?? '',
    cityEn: data.city?.en ?? '',
    countryCode: data.countryCode ?? '',
    website: data.website ?? '',
    bankAccountName: data.bank?.accountName ?? '',
    bankName: data.bank?.bankName ?? '',
    bankAccountNumber: data.bank?.accountNumber ?? '',
    bankIban: data.bank?.iban ?? '',
    bankSwiftCode: data.bank?.swiftCode ?? '',
    quotationIntroductionAr: data.quotationIntroduction?.ar ?? '',
    quotationIntroductionEn: data.quotationIntroduction?.en ?? '',
    quotationPaymentTermsAr: data.quotationPaymentTerms?.ar ?? '',
    quotationPaymentTermsEn: data.quotationPaymentTerms?.en ?? '',
    quotationFooterTextAr: data.quotationFooterText?.ar ?? '',
    quotationFooterTextEn: data.quotationFooterText?.en ?? '',
  };
}

/** A half of a pair: trimmed, and `null` when nothing is left — never `''`. */
const half = (value: string) => value.trim() || null;

/**
 * An OPTIONAL bilingual pair (D204): `null` when neither language holds
 * anything, which clears both on the server; otherwise each half trimmed, a
 * blank half `null`. Never `{ ar: '', en: '' }`, and never a pair of two
 * nulls — so a blank address can never stand in the way of saving the bank
 * details beside it.
 */
export function optionalPair(ar: string, en: string) {
  const pair = { ar: half(ar), en: half(en) };
  return pair.ar === null && pair.en === null ? null : pair;
}

/** One value: trimmed, and `null` when emptied — a blank is unrecorded, not a value. */
const single = (value: string) => value.trim() || null;

/** The PATCH body for the main form. `current` is the laboratory's saved copy. */
export function settingsBody(form: SettingsFormValues, current: TenantSettings | undefined) {
  return {
    // The name is REQUIRED in at least one language (a laboratory with no
    // readable name cannot print a document), so it is always sent as a pair
    // and the server names it if both halves are blank.
    name: { ar: half(form.nameAr), en: half(form.nameEn) },
    addressLine: optionalPair(form.addressAr, form.addressEn),
    contactEmail: single(form.contactEmail),
    contactPhone: single(form.contactPhone),
    defaultLocale: form.defaultLocale,
    timezone: form.timezone,
    currency: form.currency.toUpperCase(),
    // An empty tax rate is NOT zero. It is left as it was rather than silently
    // setting the laboratory's VAT to nothing.
    taxRatePercent: form.taxRatePercent ?? current?.taxRatePercent ?? 0,
    requirePaymentBeforeDelivery: form.requirePaymentBeforeDelivery,
    allowPartialReports: form.allowPartialReports,
    showHijriDates: form.showHijriDates,
    vatRegistrationNumber: single(form.vatRegistrationNumber),
    commercialRegistrationNumber: single(form.commercialRegistrationNumber),
    clientRequiredFields: form.clientRequiredFields,
    // An empty validity field is NOT zero, and not a change. It is left as the
    // laboratory had it rather than silently making every quotation expire on
    // the day it is written.
    quotationValidityDays: form.quotationValidityDays ?? current?.quotationValidityDays ?? undefined,
    quotationTerms: optionalPair(form.quotationTermsAr, form.quotationTermsEn),
    city: optionalPair(form.cityAr, form.cityEn),
    countryCode: single(form.countryCode),
    website: single(form.website),
    bankAccountName: single(form.bankAccountName),
    bankName: single(form.bankName),
    bankAccountNumber: single(form.bankAccountNumber),
    bankIban: single(form.bankIban),
    bankSwiftCode: single(form.bankSwiftCode),
    quotationIntroduction: optionalPair(form.quotationIntroductionAr, form.quotationIntroductionEn),
    quotationPaymentTerms: optionalPair(form.quotationPaymentTermsAr, form.quotationPaymentTermsEn),
    quotationFooterText: optionalPair(form.quotationFooterTextAr, form.quotationFooterTextEn),
  };
}

type Key = keyof SettingsFormValues;

/** The bilingual pairs, by the name the API gives them. */
export const SETTINGS_PAIRS: Readonly<Record<string, readonly [Key, Key]>> = {
  name: ['nameAr', 'nameEn'],
  addressLine: ['addressAr', 'addressEn'],
  city: ['cityAr', 'cityEn'],
  quotationTerms: ['quotationTermsAr', 'quotationTermsEn'],
  quotationIntroduction: ['quotationIntroductionAr', 'quotationIntroductionEn'],
  quotationPaymentTerms: ['quotationPaymentTermsAr', 'quotationPaymentTermsEn'],
  quotationFooterText: ['quotationFooterTextAr', 'quotationFooterTextEn'],
};

/**
 * The form values a field the server named stands for: `bankIban` is itself,
 * `addressLine` both halves, `addressLine.ar` the Arabic half.
 */
export function settingsFieldKeys(field: string): readonly Key[] {
  const [head = '', language] = field.split('.');
  const pair = SETTINGS_PAIRS[head];
  if (pair) return language === 'ar' ? [pair[0]] : language === 'en' ? [pair[1]] : pair;
  return head in EMPTY_SETTINGS_FORM ? [head as Key] : [];
}

/**
 * True while the field still holds the value the server refused. A complaint
 * disappears as soon as the person changes the field it was about, and only
 * that field: the next save decides whether it comes back.
 */
export function stillAsRefused(
  field: string,
  now: SettingsFormValues,
  refused: SettingsFormValues,
): boolean {
  return settingsFieldKeys(field).every((key) => Object.is(now[key], refused[key]));
}
